import React, { useEffect, useState } from 'react'
import { generatePath } from 'react-router'
import { NameDto } from '@/api/Common/Types'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormCheckboxField, FormField, FormReadonlyField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridExpandChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  PaymentAgentDto,
  PaymentAgentProfileDto,
  PaymentAgentSocialMedia,
  UpdatePaymentAgentProfileDto,
} from 'api/Wallet/Types/PaymentAgentDto'
import { UpdatePaymentAgent } from 'api/Wallet/Types/UpdatePaymentAgent'
import AddButton from 'components/AddButton/AddButton'
import { Link } from 'components/Link'
import MoreButton from 'components/MoreButton/MoreButton'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues, UrlPattern } from 'utils/formValidation'
import { addHttp, isHttp } from 'utils/http'
import { hasPermission } from 'utils/permissionsValidator'

import { PaymentAgentProfileModal } from './PaymentAgentProfileModal'
import { PaymentAgentSocialMediaProfileModal } from './PaymentAgentSocialMediaProfileModal'

const emptyPaymentAgentProfile: PaymentAgentProfileDto = {
  email: '',
  firstName: '',
  id: '',
  lastName: '',
  phone: '',
  socialMedia: [],
}

const initialModalState = {
  isOpen: false,
  data: emptyPaymentAgentProfile,
}

const PaymentAgentFormUI: React.FC<FormikProps<PaymentAgentFormSubmitValues> & OuterProps> = (
  props
) => {
  const [isEdit, setEdit] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<{ isOpen: boolean; data: PaymentAgentProfileDto }>(
    initialModalState
  )
  const [socialMediaModal, setSocialMediaModal] = useState<
    PaymentAgentProfileDto | UpdatePaymentAgentProfileDto | null
  >(null)
  const { values, resetForm, submitForm, permissions, errors, isValid, isSubmitting } = props
  const [gridData, setGridData] = useState(
    props.paymentAgent.profiles.map((item) => ({
      ...item,
      expanded: false,
    }))
  )

  useEffect(() => {
    setGridData(
      props.paymentAgent.profiles.map((item) => ({
        ...item,
        expanded: false,
      }))
    )
  }, [props.paymentAgent.profiles])

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setEdit(false)
    submitForm()
  }

  const handleSaveProfile = async () => {
    if (openModal.isOpen) {
      const updatedProfile: UpdatePaymentAgentProfileDto = {
        ...openModal.data,
        socialMedia: openModal.data.socialMedia.map((x: PaymentAgentSocialMedia) => ({
          typeId: x.socialMediaType.id,
          value: x.value,
        })),
      }
      await props.handleSaveProfile(updatedProfile)
    }
    setOpenModal(initialModalState)
  }

  const handleSaveProfileMedia = async (v: UpdatePaymentAgentProfileDto) => {
    await props.handleSaveProfile(v)
    setSocialMediaModal(emptyPaymentAgentProfile)
  }

  const getPowerOfAttorneyUrl = () => {
    const powerOfAttorneyUrl = values?.powerOfAttorneyUrl || ''
    if (!isHttp(powerOfAttorneyUrl)) {
      return addHttp(powerOfAttorneyUrl)
    }
    return powerOfAttorneyUrl
  }

  const renderMenu = () => (
    <DropdownMenuItem
      onSelect={() => props.setAuditLogModal(true)}
      data-test='audit-log-menu-button'
    >
      Audit Log
    </DropdownMenuItem>
  )

  const expandChange = (event: GridExpandChangeEvent) => {
    event.dataItem[event.target.props.expandField as string] = event.value
    const updatedGridData = gridData.map((item) => {
      if (item === event.dataItem) {
        return { ...item, expanded: event.value }
      }
      return item
    })
    setGridData(updatedGridData)
  }

  return (
    <React.Fragment>
      <PaymentAgentProfileModal
        data={openModal.data}
        isOpen={openModal.isOpen}
        handleSaveProfile={handleSaveProfile}
        setData={(data) => setOpenModal({ isOpen: true, data })}
        onClose={() => setOpenModal(initialModalState)}
      />
      {socialMediaModal && (
        <PaymentAgentSocialMediaProfileModal
          isOpen={!!socialMediaModal}
          onClose={() => setSocialMediaModal(null)}
          data={socialMediaModal as PaymentAgentProfileDto}
          handleSaveProfile={handleSaveProfileMedia}
          socialMediaTypes={props.socialMediaTypes}
        />
      )}
      <MainTemplate>
        <Form>
          <DataGrid
            rightAddon={
              <ButtonGroup>
                {!isEdit ? (
                  <Button
                    type='button'
                    disabled={
                      isSubmitting ||
                      !hasPermission(permissions, PermissionsEnum.CanEditPaymentAgent)
                    }
                    onClick={() => setEdit(true)}
                    data-test='edit-button'
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      variant='outline'
                      type='button'
                      onClick={cancel}
                      data-test='cancel-button'
                    >
                      Cancel
                    </Button>
                    <Button
                      key='button'
                      type='button'
                      onClick={handleSubmitForm}
                      disabled={!isValid || isSubmitting}
                      data-test='save-button'
                    >
                      Save
                      {isSubmitting && <Spinner data-icon='inline-start' />}
                    </Button>
                  </>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        aria-label='More options'
                        disabled={isSubmitting}
                      />
                    }
                  >
                    <ChevronDown />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            }
          >
            <DataColumn>
              <DataCard columns={2} title='Main'>
                <FormReadonlyField
                  label='Client'
                  value={props.paymentAgent.client.name?.toString()}
                  link={generatePath(links.recordsClients.detailsUrl as string, {
                    id: props.paymentAgent.client?.id,
                    slug: 'details',
                  })}
                />

                <FormField name='name' label='Name' readOnly={!isEdit} />
                <FormField name='description' label='Description' readOnly={!isEdit} />
                <FormField
                  name='website'
                  label='Website'
                  error={errors.website}
                  readOnly={!isEdit}
                />
                <FormField
                  name='powerOfAttorneyEmail'
                  label='Payment Agent Email'
                  readOnly={!isEdit}
                />
                <FormField name='withdrawalEmail' label='Withdrawal Email CC' readOnly={!isEdit} />
                <FormField
                  label='Power Of Attorney Document Url'
                  name='powerOfAttorneyUrl'
                  readOnly={!isEdit}
                  error={errors.powerOfAttorneyUrl}
                  link={{ target: '_blank', to: getPowerOfAttorneyUrl() }}
                />
                <FormCheckboxField name='isActive' label='Active' readOnly={!isEdit} />

                <FormField
                  name='singleTransactionLimit'
                  label='Single Transaction Limit'
                  type='number'
                  readOnly={!isEdit}
                />
                <FormField
                  name='dailyTransactionLimit'
                  label='Daily Transaction Limit'
                  type='number'
                  readOnly={!isEdit}
                />
                <FormReadonlyField
                  label="Today's Transaction"
                  value={props.paymentAgent.todaysTransaction}
                />
              </DataCard>

              <DataCard title='Profiles'>
                <div className='flex flex-col gap-3'>
                  <Grid
                    data={gridData}
                    resizable
                    onExpandChange={expandChange}
                    expandField='expanded'
                    detail={(dataItem) => {
                      return (
                        <div className='space-y-2 py-2'>
                          <div className='font-semibold'>Social Media</div>
                          {(dataItem.dataItem as PaymentAgentProfileDto).socialMedia.map(
                            (paymentAgentSocialMedia: PaymentAgentSocialMedia) => {
                              return (
                                <div
                                  key={paymentAgentSocialMedia.id}
                                  className='flex flex-wrap items-center gap-2 text-sm'
                                >
                                  <span className='font-medium'>
                                    {paymentAgentSocialMedia?.socialMediaType.name}:
                                  </span>
                                  <Link
                                    className='text-primary underline'
                                    to={paymentAgentSocialMedia?.value}
                                  >
                                    {paymentAgentSocialMedia?.value}
                                  </Link>
                                </div>
                              )
                            }
                          )}
                        </div>
                      )
                    }}
                  >
                    <Column width={250} field='firstName' title='Firstname' />
                    <Column width={250} field='lastName' title='Lastname' />
                    <Column width={250} field='email' title='Email' />
                    <Column width={250} field='phone' title='Phone' />
                    <Column
                      title=''
                      width={50}
                      cell={(gridCellProps: GridCellProps) => (
                        <td>
                          <MoreButton
                            items={[
                              {
                                name: 'Edit',
                                disabled: !isEdit,
                                onClick: () => {
                                  setOpenModal({
                                    isOpen: true,
                                    data: gridCellProps.dataItem as PaymentAgentProfileDto,
                                  })
                                },
                              },
                              {
                                name: 'Delete',
                                disabled: !isEdit,
                                onClick: () => props.deleteProfile(gridCellProps.dataItem.id),
                              },
                              {
                                name: `${
                                  (gridCellProps.dataItem as PaymentAgentProfileDto).socialMedia
                                    .length
                                    ? 'Edit'
                                    : 'Add'
                                } Social Media`,
                                disabled: !isEdit,
                                onClick: () => {
                                  setSocialMediaModal(
                                    gridCellProps.dataItem as UpdatePaymentAgentProfileDto
                                  )
                                },
                              },
                            ]}
                          />
                        </td>
                      )}
                    />
                  </Grid>
                  <AddButton
                    disabled={!isEdit}
                    text='Add profile'
                    onClick={() => setOpenModal({ isOpen: true, data: emptyPaymentAgentProfile })}
                  />
                </div>
              </DataCard>
            </DataColumn>
          </DataGrid>
        </Form>
      </MainTemplate>
    </React.Fragment>
  )
}

interface OuterProps {
  paymentAgent: PaymentAgentDto
  permissions: PermissionDto[]
  socialMediaTypes: NameDto[]

  onSubmit(values: FormSubmitValues<PaymentAgentFormSubmitValues>): Promise<void>

  forceRefresh: () => void

  handleSaveProfile(values: UpdatePaymentAgentProfileDto): Promise<void>

  deleteProfile(paymentAgentProfileId: string): Promise<void>

  setAuditLogModal(value: boolean): void
}

export type PaymentAgentFormSubmitValues = UpdatePaymentAgent

export const PaymentAgentForm = withFormik<OuterProps, PaymentAgentFormSubmitValues>({
  mapPropsToValues: ({ paymentAgent }) => {
    const currentPaymentAgent: PaymentAgentDto = paymentAgent || {}
    return {
      ...currentPaymentAgent,
      introducingBrokerFundingTypeId: currentPaymentAgent.introducingBrokerFundingType.id,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PaymentAgentFormSubmitValues> = {}

    if (!!values.powerOfAttorneyUrl && !UrlPattern.test(values.powerOfAttorneyUrl)) {
      errors.powerOfAttorneyUrl = 'Invalid URL. Please start with https://.'
    }

    if (!!values.website && !UrlPattern.test(values.website)) {
      errors.website = 'Invalid URL. Please start with https://.'
    }

    if (!values.powerOfAttorneyUrl) {
      errors.powerOfAttorneyUrl = 'Invalid URL. Please start with https://.'
    }
    if (!!values.powerOfAttorneyUrl && !UrlPattern.test(values.powerOfAttorneyUrl)) {
      errors.powerOfAttorneyUrl = 'Invalid URL. Please start with https://.'
    }
    return errors
  },
})(PaymentAgentFormUI)
